"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";

// Couleurs de coloration syntaxique (sur fond sombre).
const TOKEN_CLASS = {
    kw: "text-fuchsia-400",
    fn: "text-sky-400",
    str: "text-emerald-400",
    num: "text-amber-300",
    com: "text-gray-500 italic",
    var: "text-sky-200",
    op: "text-gray-400",
    txt: "text-gray-200",
};

export default function PythonLiveRepl() {
    const t = useTranslations();
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const inView = useInView(ref, { margin: "-120px" });

    // Le code se construit ligne par ligne, puis "s'exécute", puis la sortie
    // apparaît. Les fragments lisibles (commentaires, chaînes, sortie) viennent
    // de l'i18n ; les accolades f-string restent littérales pour ne pas casser ICU.
    const imcPrefix = t("Python_live_s_imc_prefix");
    const CODE = [
        [["poids", "var"], [" = ", "op"], ["70", "num"], ["   # " + t("Python_live_c_weight"), "com"]],
        [["taille", "var"], [" = ", "op"], ["1.75", "num"], [" # " + t("Python_live_c_height"), "com"]],
        [],
        [["imc", "var"], [" = ", "op"], ["poids", "txt"], [" / ", "op"], ["taille", "txt"], [" ** ", "op"], ["2", "num"]],
        [],
        [["print", "fn"], ["(", "op"], ['f"', "str"], [imcPrefix, "str"], ["{imc:.1f}", "str"], ['"', "str"], [")", "op"]],
        [],
        [["if", "kw"], [" imc ", "txt"], ["<", "op"], [" 25", "num"], [":", "op"]],
        [["    ", "txt"], ["print", "fn"], ["(", "op"], ['"' + t("Python_live_s_ok") + '"', "str"], [")", "op"]],
        [["else", "kw"], [":", "op"]],
        [["    ", "txt"], ["print", "fn"], ["(", "op"], ['"' + t("Python_live_s_watch") + '"', "str"], [")", "op"]],
    ];
    const OUTPUT = [imcPrefix + "22.9", t("Python_live_s_ok")];

    const isBlank = (line) => line.length === 0;

    const [lines, setLines] = useState(0); // nb de lignes de code affichées
    const [running, setRunning] = useState(false);
    const [ran, setRan] = useState(false); // sortie révélée
    const [outCount, setOutCount] = useState(0);
    const [runId, setRunId] = useState(0);

    useEffect(() => {
        if (reduce) {
            setLines(CODE.length);
            setRunning(false);
            setRan(true);
            setOutCount(OUTPUT.length);
            return;
        }
        if (!inView) return;

        const timers = [];
        setLines(0);
        setRunning(false);
        setRan(false);
        setOutCount(0);

        let d = 450;
        CODE.forEach((line, i) => {
            timers.push(setTimeout(() => setLines(i + 1), d));
            d += isBlank(line) ? 80 : 250;
        });
        d += 550;
        timers.push(setTimeout(() => setRunning(true), d));
        d += 850;
        timers.push(
            setTimeout(() => {
                setRunning(false);
                setRan(true);
            }, d),
        );
        OUTPUT.forEach((_, i) => {
            d += 340;
            timers.push(setTimeout(() => setOutCount(i + 1), d));
        });
        d += 2600;
        timers.push(setTimeout(() => setRunId((r) => r + 1), d));

        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView, runId, reduce]);

    return (
        <section id="python-live" className="pt-16" ref={ref}>
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Python_live_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Python_live_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-indigo-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon icon="logos:python" className="w-3.5 h-3.5" aria-hidden="true" />
                        imc.py
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-sky-500/15 ring-1 ring-sky-400/30 px-2.5 py-0.5">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-300">
                            {t("Python_live_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative grid lg:grid-cols-2">
                    {/* ÉDITEUR */}
                    <div className="p-5 sm:p-7 font-mono text-[12.5px] sm:text-[13px] leading-relaxed lg:border-r border-white/10 min-h-[320px]">
                        {CODE.map((line, i) => {
                            if (i >= lines) return null;
                            const current = i === lines - 1;
                            return (
                                <motion.div
                                    key={i}
                                    initial={reduce ? false : { opacity: 0, x: -6 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.25, ease: "easeOut" }}
                                    className="flex"
                                >
                                    <span className="select-none w-7 shrink-0 text-right pr-3 text-gray-600">
                                        {i + 1}
                                    </span>
                                    <span className="whitespace-pre break-words">
                                        {isBlank(line) ? " " : null}
                                        {line.map(([text, type], j) => (
                                            <span key={j} className={TOKEN_CLASS[type]}>
                                                {text}
                                            </span>
                                        ))}
                                        {current && !ran && !running && (
                                            <motion.span
                                                aria-hidden
                                                className="inline-block w-2 h-[14px] bg-sky-400 ml-0.5 align-middle"
                                                animate={{ opacity: [1, 1, 0, 0] }}
                                                transition={{
                                                    repeat: Infinity,
                                                    duration: 1,
                                                    times: [0, 0.5, 0.5, 1],
                                                }}
                                            />
                                        )}
                                    </span>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* CONSOLE */}
                    <div className="p-5 sm:p-7 font-mono text-[12.5px] sm:text-[13px] leading-relaxed bg-black/20">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-gray-500 mb-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-600" />
                            {t("Python_live_console")}
                        </div>

                        <div className="text-gray-300">
                            <span className="text-sky-400">$</span>{" "}
                            <span className="text-gray-400">python</span> imc.py
                        </div>

                        {running && (
                            <div className="mt-3 flex items-center gap-2 text-sky-300">
                                <span className="inline-block w-3 h-3 rounded-full border-2 border-sky-400/30 border-t-sky-400 animate-spin" />
                                <span>{t("Python_live_running")}</span>
                            </div>
                        )}

                        {ran && (
                            <div className="mt-2 flex flex-col gap-1">
                                {OUTPUT.map((out, i) =>
                                    i < outCount ? (
                                        <motion.div
                                            key={i}
                                            initial={reduce ? false : { opacity: 0, y: 4 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.25 }}
                                            className="text-emerald-300"
                                        >
                                            {out}
                                        </motion.div>
                                    ) : null,
                                )}
                                {outCount >= OUTPUT.length && (
                                    <motion.div
                                        initial={reduce ? false : { opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="mt-2 flex items-center gap-2 text-gray-500"
                                    >
                                        <span className="text-emerald-400">✓</span>
                                        <span>{t("Python_live_exited")}</span>
                                    </motion.div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Python_live_caption")}
            </p>
        </section>
    );
}
